import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { HelpersService } from './helper.service';
import { HttpQuery } from './http-query';
import { ProgressBarService } from './progress-bar.service';

export interface BaseAPIObject<T> {
  data?: T;
  statusCode: number;
  message: string;
  errors?: any;
}

export interface BaseAPIArray<T> {
  data: T[];
  page: number;
  total: number;
  perPage: number;
  lastPage: number;
  nextPage: string;
  previousPage: string;
  statusCode: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class BaseApiService<T> implements Resolve<T>{

  constructor(
    public readonly http: HttpClient,
    public readonly helper: HelpersService,
    public readonly loading: ProgressBarService
  ) {
    this.baseUrl = '/api'
    this.onDataChanged = new BehaviorSubject(this.data)
    this.onDataLengthChanged = new BehaviorSubject(this.dataLength)
    this.onQueryChange = new BehaviorSubject(this.query)
    this.loading.setMode('query');
    setTimeout(() => {
      if (this.endpoint!='' && this.query) {
        this.onQueryChange.subscribe((query) => {
          this.query = query;
          this.getAll(query)
        })
      }
    });
  }

  private baseUrl = '/api';
  endpoint: string;
  query: HttpQuery;
  private data: T[];
  private dataLength: number;
  private currentData: T;
  onDataChanged: BehaviorSubject<any>;
  onDataLengthChanged: BehaviorSubject<any>;
  onQueryChange: BehaviorSubject<any>;


  resolve(): Observable<any> | Promise<any> | any {
    return this.getAll(this.query)
  }

  url(): string {
    return [this.baseUrl, this.endpoint].join('/');
  }
  url_id(id): string {
    return [this.baseUrl, this.endpoint, id].join('/');
  }
  url_custom(c): string {
    return [this.baseUrl, this.endpoint, c].join('/');
  }
  url_array(c: string[]): string {
    const baseArray = [this.baseUrl, this.endpoint];
    c.map((a) => baseArray.push(a));
    return baseArray.join('/');
  }

  getAll(query?: HttpQuery, notifyChange: Boolean = true): Promise<T[]> {
    return new Promise((resolve) => {
      const url = this.url();
      const params = query ? this.helper.generateParams(query) : {};
      if (Object.keys(params).length == 0) {
        return resolve(this.data)
      }
      this.loading.show()
      this.http.get<BaseAPIArray<T>>(url, { params })
      .subscribe({
        next: ({data, total}) => {
          this.data = data;
          this.dataLength = total;
          if (notifyChange){
            this.onDataChanged.next(this.data);
            this.onDataLengthChanged.next(this.dataLength)
          }
          this.loading.hide();
          return resolve(this.data)
        },
        error: (err) => this.handleError(err)
      })
    })
  }

  getById(id: string, query: HttpQuery = {}): Promise<T> {
    return new Promise((resolve) => {
      const url = this.url_id(id);
      const params = query ? this.helper.generateParams(query) : null;
      if (Object.keys(params).length == 0) {
        return resolve(null)
      }
      this.loading.show();
      this.http.get<BaseAPIObject<T>>(url, { params }).subscribe({
        next: ({data}) => {
          this.currentData = data;
          this.loading.hide();
          return resolve(this.currentData)
        },
        error: (err) => this.handleError(err)
      })
    })
  }

  insert(data: any): Promise<T>{
    return new Promise((resolve) => {
      const url = this.url();
      this.loading.show();
      this.http.post<BaseAPIObject<T>>(url, data).pipe(share()).subscribe({
        next: ({data}) => {
          this.getAll(this.query)
          this.loading.hide()
          return resolve(data)
        },
        error: (err) => this.handleError(err)
      })
    })
  }

  updateById(id: string, data: any): Promise<T> {
    return new Promise((resolve) => {
      const url = this.url_id(id);
      this.loading.show();
      this.http.put<BaseAPIObject<T>>(url, data).pipe(share()).subscribe({
        next: ({data}) => {
          this.getAll(this.query)
          return resolve(data)
        },
        error: (err) => this.handleError(err)
      })
      this.loading.hide();
    })
  }

  removeById(id: string): Promise<any> {
    return new Promise((resolve) => {
      const url = this.url_id(id);
      this.loading.show();
      this.http.delete<BaseAPIObject<T>>(url).subscribe({
        next: ({data}) => {
          this.getAll(this.query)
          return resolve(data)
        },
        error: (err) => this.handleError(err)
      })
    })
  }

  removeAll(): Promise<any>{
    return new Promise((resolve) => {
      const url = this.url();
      this.http.delete<BaseAPIObject<T>>(url).subscribe({
        next: ({data}) => {
        this.getAll(this.query)
        return resolve(data)
      },
        error: err => this.handleError(err)
      })
    });
  }

  handleError(err){
    this.loading.hide();
    return Promise.reject(err)
  }
}
